import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
;import { Download, Share2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { usePWAInstall } from "../hooks/usePWAInstall"

const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)

const SCREENSHOTS = [
  "/assets/screenshots/mockup-left.png",
  "/assets/screenshots/mockup-center.png",
  "/assets/screenshots/mockup-right.png",
]

const HERO_SCALLOPS = Array.from({ length: 24 })

const FEATURES = [
  "App exclusive features",
  "Faster checkout",
  "Exclusive app-only deals",
  "Offline browsing",
]

export default function PWAInstallPrompt() {
  const { t } = useTranslation()
  const { isInstalled, promptInstall, hasPrompt } = usePWAInstall()
  const [status, setStatus] = useState("sheet")
  const [iosDismissed, setIosDismissed] = useState(false)
  const [splashDone, setSplashDone] = useState(() => !!window.__splashDone)
  const [featureIndex, setFeatureIndex] = useState(0)

  useEffect(() => {
    if (status !== "sheet") return
    const id = setInterval(() => setFeatureIndex(i => (i + 1) % FEATURES.length), 2200)
    return () => clearInterval(id)
  }, [status])

  useEffect(() => {
    if (window.__splashDone) return
    const handler = () => setSplashDone(true)
    window.addEventListener("splashDone", handler, { once: true })
    return () => window.removeEventListener("splashDone", handler)
  }, [])

  if (!splashDone) return null
  if (isInstalled) return null

  if (isIOS) {
    if (iosDismissed) return null

    return (
      <AnimatePresence>
        <div key="ios-banner" className="fixed bottom-20 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 80, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="pointer-events-auto relative w-full max-w-sm rounded-2xl border border-gray-100 bg-white p-4 shadow-2xl dark:border-border-100 dark:bg-background-200"
          >
            <div className="absolute -bottom-[9px] left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-b border-r border-gray-100 bg-white dark:border-border-100 dark:bg-background-200" />
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary-600 shadow-md">
                <Download className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold leading-tight text-gray-900 dark:text-white">{t("pwa.ios.title")}</p>
                <div className="mt-2.5 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">1</span>
                    <p className="flex flex-wrap items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                      {t("pwa.ios.step1")} <Share2 className="inline h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">2</span>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t("pwa.ios.step2")}</p>
                  </div>
                </div>
              </div>
            </div>
            <button onClick={() => setIosDismissed(true)} className="mt-3 w-full py-1 text-center text-xs text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200">
              {t("pwa.notNow")}
            </button>
          </motion.div>
        </div>
      </AnimatePresence>
    )
  }

  if (!hasPrompt) return null

  return (
    <AnimatePresence mode="popLayout">
      {status === "sheet" && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] bg-black/60"
            onClick={() => setStatus("collapsed")}
          />

          <motion.div
            key="sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "110%" }}
            transition={{ type: "spring", damping: 32, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-[71]"
          >
            <div className="relative">
              <div className="w-full pt-14" onClick={() => setStatus("collapsed")}>
                <button
                  className="pointer-events-auto absolute left-1/2 top-1 z-30 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full bg-zinc-900/90 text-white shadow-lg backdrop-blur-sm"
                  aria-label={t("common.cancel")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="pointer-events-auto overflow-hidden rounded-t-[28px] bg-white dark:bg-zinc-900 shadow-[0_18px_60px_rgba(0,0,0,0.35)] h-[60vh] flex flex-col">
                <div className="relative overflow-hidden bg-[linear-gradient(180deg,#d8caff_0%,#a277ff_48%,#7c3aed_100%)]">
                  {/* Images already have frame + tilt baked in — no rotation applied */}
                  <div className="grid grid-cols-3 gap-0.5 top-0">
                    <img src={SCREENSHOTS[0]} alt="" loading="eager" className="object-cover object-top z-[1] justify-self-end" />
                    <img src={SCREENSHOTS[1]} alt="" loading="eager" className="object-cover object-top z-[2] justify-self-center" />
                    <img src={SCREENSHOTS[2]} alt="" loading="eager" className="object-cover object-top z-[1] justify-self-start" />
                  </div>

                  {/* Scalloped bottom edge */}
                  <div className="absolute inset-x-0 bottom-[-10px] z-10 flex justify-between">
                    {HERO_SCALLOPS.map((_, i) => (
                      <span key={i} className="h-5 w-5 rounded-full bg-white dark:bg-zinc-900" />
                    ))}
                  </div>
                </div>

                <div className="relative card px-5 pb-7 pt-12">
                  {/* Logo — no white card, just the icon overlapping the boundary */}
                  <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
                    <img
                      src="/assets/icons/maskable_icon_x96.png"
                      alt="App icon"
                      className="block h-14 w-14 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
                    />
                  </div>

                  <div className="mx-auto max-w-[430px]">
                    <p className="text-center text-sm font-medium text-zinc-600 dark:text-zinc-300">
                      {t("pwa.installDescription")}
                    </p>
                    <div className="relative mt-3 h-[2.6rem] overflow-hidden">
                      <AnimatePresence mode="wait">
                        <motion.h2
                          key={featureIndex}
                          initial={{ y: 36, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -36, opacity: 0 }}
                          transition={{ duration: 0.35, ease: "easeInOut" }}
                          className="absolute inset-x-0 text-center text-[1.65rem] font-extrabold leading-[1.1] tracking-[-0.03em] text-zinc-900 dark:text-white"
                        >
                          {FEATURES[featureIndex]}
                        </motion.h2>
                      </AnimatePresence>
                    </div>
                    <p className="mt-2 text-center text-[11px] font-medium uppercase tracking-[0.26em] text-zinc-500 dark:text-zinc-400">
                      {t("pwa.installTitle")}
                    </p>

                    <div className="mt-6 space-y-3">
                      <button
                        onClick={() => promptInstall()}
                        className="w-full rounded-xl bg-primary-700 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary-200 dark:shadow-primary-900/40 transition-colors hover:bg-primary-800 active:bg-primary-900"
                      >
                        {t("pwa.install")}
                      </button>
                      <button
                        onClick={() => setStatus("collapsed")}
                        className="w-full py-1 text-sm font-medium text-primary-700 dark:text-primary-400 transition-colors hover:text-primary-800 dark:hover:text-primary-300"
                      >
                        {t("pwa.continueOnWeb")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {status === "collapsed" && (
        <div key="fab" className="fixed bottom-32 right-4 z-50 pointer-events-none">
          <motion.button
            layoutId="pwa-install-fab"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.92 }}
            transition={{ type: "spring", damping: 30, stiffness: 400 }}
            onClick={() => setStatus("sheet")}
            className="pointer-events-auto flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-xl shadow-primary-200 transition-colors hover:bg-primary-700 active:bg-primary-800 dark:shadow-primary-900/40"
            aria-label={t("pwa.installButton")}
          >
            <Download className="h-4 w-4" />
            {t("pwa.installButton")}
          </motion.button>
        </div>
      )}
    </AnimatePresence>
  )
}
